// Phone / tablet step pager for the full-screen builders (KAN-763 polish): an
// iOS-style PUSH. Each step is a full-size absolutely-positioned page inside a
// clipped stage; going forward the new page slides in from the right over the
// old one (which drifts 30% left under a soft dim), going back is the exact
// reverse. Presentation only: the host decides what is on each page.
//
// Safari rules (AGENTS.md, iOS transition guard): transform is `none` at rest
// (a page is only ever off-centre WHILE a transition runs), NO will-change /
// translateZ / contain / isolation, and the outgoing page is unmounted by a
// TIMER (duration + slack), never by `transitionend` alone — iOS drops those.
// Each page owns its own scroller (so a step's scroll position is its own).
// Never move content with a transform inside an AutoHeight: the pager is not
// inside one, and pages are the only transformed boxes.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { foundation } from '../../../ui';
import { components } from '../../../ui/theme/componentTokens';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import { flowBodyScroller, flowBodyInnerStyle } from './FullScreenFlowBody';

const { color, shadow } = foundation;

/** Timings (ms): the push, the reduced-motion crossfade, and the unmount slack. */
export const STEP_PAGER_MOTION = { push: 320, fade: 150, slack: 80 };
const EASE = 'cubic-bezier(.2,.8,.2,1)';
// The dim over the page that is being pushed under: the shared scrim at 15%.
const DIM_OPACITY = 0.15;
const BEHIND_SHIFT = 'translateX(-30%)';
const AHEAD_SHIFT = 'translateX(100%)';

const stage = { position: 'relative', flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' };
const pageBase = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  background: color.neutral.bg,
  boxSizing: 'border-box',
};
const topSlot = { flexShrink: 0 };
const dimBase = {
  position: 'absolute',
  inset: 0,
  background: components.overlay.scrim,
  pointerEvents: 'none',
};

function pageStyle(pos, reduced, ms, moving) {
  if (reduced) {
    return {
      ...pageBase,
      opacity: pos === 'current' ? 1 : 0,
      transform: 'none',
      transition: `opacity ${ms}ms linear`,
    };
  }
  let transform = 'none';
  if (pos === 'before') transform = BEHIND_SHIFT;
  else if (pos === 'after') transform = AHEAD_SHIFT;
  // The lift shadow only while a push runs; a page at rest has none.
  return { ...pageBase, ...(moving ? { boxShadow: shadow.lg } : {}), transform, transition: `transform ${ms}ms ${EASE}` };
}

function PagerPage({ n, pos, reduced, ms, layout, page, memory }) {
  const nodeRef = useRef(null);
  const restoreRef = useRef(pos === 'before');
  const externalRef = page.scrollRef;
  const setNode = useCallback((node) => {
    nodeRef.current = node;
    if (typeof externalRef === 'function') externalRef(node);
    else if (externalRef) externalRef.current = node;
  }, [externalRef]);

  // Going BACK to a page restores where the user left it; a page entered
  // going forward starts at the top. The position is saved as the page leaves.
  useLayoutEffect(() => {
    const node = nodeRef.current;
    const saved = memory.current;
    if (node && restoreRef.current && saved[n]) node.scrollTop = saved[n];
    return () => { if (node) saved[n] = node.scrollTop; };
  }, [n, memory]);

  // `moving` = a push is running for this page (parked, or its position just
  // changed). Cleared by a timer, never by transitionend (iOS drops those).
  const [moving, setMoving] = useState(pos !== 'current');
  const lastPos = useRef(pos);
  useLayoutEffect(() => {
    if (lastPos.current === pos) return;
    lastPos.current = pos;
    setMoving(true);
  }, [pos]);
  useEffect(() => {
    if (!moving) return undefined;
    const timer = setTimeout(() => setMoving(false), ms + STEP_PAGER_MOTION.slack);
    return () => clearTimeout(timer);
  }, [moving, pos, ms]);

  const current = pos === 'current';
  return (
    <div
      style={pageStyle(pos, reduced, ms, moving)}
      data-testid={`fullscreen-step-page-${n}`}
      data-step={n}
      data-state={pos}
      inert={current ? undefined : true}
      aria-hidden={current ? undefined : true}
    >
      {page.top ? <div style={topSlot}>{page.top}</div> : null}
      <div
        ref={setNode}
        style={flowBodyScroller}
        data-testid={current ? 'fullscreen-flow-body' : `fullscreen-flow-body-${n}`}
        data-layout={layout}
      >
        <div style={flowBodyInnerStyle(layout)}>{page.content}</div>
      </div>
      {reduced ? null : (
        <div
          aria-hidden
          data-testid={`fullscreen-step-dim-${n}`}
          style={{ ...dimBase, opacity: pos === 'before' ? DIM_OPACITY : 0, transition: `opacity ${ms}ms ${EASE}` }}
        />
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {'phone'|'tablet'} props.layout
 * @param {number} props.step - the step to show (1-based; higher = further right).
 * @param {Record<number, {content: import('react').ReactNode, top?: import('react').ReactNode, scrollRef?: import('react').Ref<HTMLDivElement>}|null>} props.pages
 *   one entry per step; a missing/null entry is not rendered.
 * @param {string} [props.testId]
 */
export default function FullScreenStepPager({ layout, step, pages, testId = 'fullscreen-step-pager' }) {
  const reduced = useReducedMotion();
  const ms = reduced ? STEP_PAGER_MOTION.fade : STEP_PAGER_MOTION.push;
  // `shown` LAGS `step` by one synchronous commit: the new page mounts at its
  // parked position first, then (after a forced style flush) `shown` catches up
  // and the CSS transition runs from the parked position to rest.
  const [shown, setShown] = useState(step);
  const [leaving, setLeaving] = useState([]);
  const stageRef = useRef(null);
  const memory = useRef({});

  useLayoutEffect(() => {
    if (step === shown) return;
    // Read layout so the parked page's computed style is resolved BEFORE the
    // style change that starts the transition.
    if (stageRef.current) void stageRef.current.offsetWidth;
    setLeaving((list) => [...list.filter((n) => n !== step && n !== shown), shown]);
    setShown(step);
  }, [step, shown]);

  // Unmount the outgoing page on a TIMER (never only on transitionend).
  useEffect(() => {
    if (leaving.length === 0) return undefined;
    const timer = setTimeout(() => setLeaving([]), ms + STEP_PAGER_MOTION.slack);
    return () => clearTimeout(timer);
  }, [leaving, ms]);

  const mounted = Array.from(new Set([step, shown, ...leaving]))
    .filter((n) => pages[n])
    .sort((a, b) => a - b);

  return (
    <div ref={stageRef} style={stage} data-testid={testId} data-layout={layout}>
      {mounted.map((n) => {
        let pos = 'current';
        if (n < shown) pos = 'before';
        else if (n > shown) pos = 'after';
        return <PagerPage key={n} n={n} pos={pos} reduced={reduced} ms={ms} layout={layout} page={pages[n]} memory={memory} />;
      })}
    </div>
  );
}
